import { computed, onMounted, ref, watch } from 'vue';
import { isEmpty, uniq } from 'lodash';
import { useI18n, useColors } from '@/composables';
import { $emitter } from '@/services/emitter';

type ThreePanelBrand = {
  name: string;
  selected?: boolean;
  picked?: boolean;
  data: string[];
};

type ThreePanelSelectedItem = {
  brand: string;
  name: string;
  picked: boolean;
};

type ThreePanelConditionEntry = {
  values: string[];
  predicate: string;
  includeUnknown?: boolean;
};

type FlowConditions = Record<string, ThreePanelConditionEntry>;

type ThreePanelPredicate = {
  value: string;
  label: string;
};

type FlowThreePanelConditionProps = {
  conditions: FlowConditions;
  prefix: string;
  loading: boolean;
  title: string;
  labelHelp?: string;
  data: ThreePanelBrand[];
  predicates?: ThreePanelPredicate[];
};

export const useFlowThreePanelCondition = (props: FlowThreePanelConditionProps) => {
  const { t } = useI18n();
  const $colors = useColors();

  const focusedItem = ref<ThreePanelBrand | Record<string, never>>({});
  const selected = ref<ThreePanelSelectedItem[]>([]);
  const predicate = ref('EQUALS');
  const includeUnknown = ref(false);
  const brandSelectAllIndex = ref<Record<string, boolean>>({});
  const selectedDataByCategory = ref<Record<string, ThreePanelBrand>>({});

  const defaultPredicates = computed<ThreePanelPredicate[]>(() => [
    { value: 'EQUALS', label: t('Equals') },
    { value: 'NOT_EQUALS', label: t('Not equal') },
  ]);

  const panelPredicates = computed(() => props.predicates || defaultPredicates.value);

  const selectedKeys = computed(() => selected.value.map((item) => item.name));

  const selectedKeysIndex = computed(() => {
    const result: Record<string, boolean> = {};
    selectedKeys.value.forEach((key) => {
      result[key] = true;
    });
    return result;
  });

  const selectedBrands = computed(() => selected.value.map((item) => item.brand));

  const subItems = computed(() =>
    focusedItem.value?.data?.map((model) => ({
      name: model,
      brand: focusedItem.value.name,
    })) || [],
  );

  const getModelsDict = () => {
    const result: Record<string, string> = {};
    props.data.forEach((brand) => {
      brand.data.forEach((model) => {
        result[model] = brand.name;
      });
    });
    return result;
  };

  const isBrand = (name: string) => props.data.find((item) => item.name === name);

  const isSelected = (name: string) => selectedKeys.value.indexOf(name) !== -1;

  const updateBrandSelectAllIndex = (item: ThreePanelBrand = focusedItem.value as ThreePanelBrand) => {
    if (!item?.data) return;
    brandSelectAllIndex.value[item.name] = item.data.every(
      (dataItem) => selectedKeysIndex.value[dataItem],
    );
  };

  const add = (item: ThreePanelSelectedItem, updateIndex = true) => {
    if (!isSelected(item.name)) {
      selected.value.push(item);
    }

    if (updateIndex) {
      updateBrandSelectAllIndex();
    }
  };

  const remove = (name?: string, brand?: string, updateIndex = true) => {
    const names = name
      ? [name]
      : selected.value.filter((item) => item.brand === brand).map((item) => item.name);

    const indexes: number[] = [];
    let counter = 0;

    names.forEach((entryName) => {
      if (selectedKeys.value.indexOf(entryName) !== -1) {
        indexes.push(selectedKeys.value.indexOf(entryName));
      }
    });

    indexes.sort((left, right) => (left > right ? 1 : left < right ? -1 : 0));

    selected.value = selected.value.filter(
      (selectedItem, index) => !(index === indexes[counter] && (counter = counter + 1)),
    );

    if (updateIndex) {
      updateBrandSelectAllIndex();
    }
  };

  const updateSelected = (values: string[]) => {
    const modelsDict = getModelsDict();
    values.forEach((entry) => {
      const brand = isBrand(entry);
      if (brand) {
        selected.value = selected.value.concat(
          brand.data.map((model) => ({
            brand: entry,
            name: model,
            picked: true,
          })),
        );
        updateBrandSelectAllIndex(brand);
      } else {
        selected.value.push({
          brand: modelsDict[entry],
          name: entry,
          picked: true,
        });
      }
    });
  };

  const toggle = (item: ThreePanelBrand & { brand?: string }) => {
    if (item.data) {
      focusedItem.value = item;
    } else if (!isSelected(item.name)) {
      add(item as ThreePanelSelectedItem);
    } else {
      remove(item.name);
    }
  };

  const selectAll = (item: ThreePanelBrand) => {
    if (item.data) {
      focusedItem.value = item;
    }

    const currentSubItems = (focusedItem.value?.data || []).map((model) => ({
      name: model,
      brand: focusedItem.value.name as string,
    }));

    if (!brandSelectAllIndex.value[item.name]) {
      const newSubItems = currentSubItems.filter(
        (subItem) => !selectedKeysIndex.value[subItem.name],
      );
      selected.value = selected.value.concat(newSubItems as ThreePanelSelectedItem[]);
    } else {
      remove(undefined, focusedItem.value.name as string, false);
    }

    updateBrandSelectAllIndex();
  };

  const selectedBrandsUnique = computed(() => {
    const uniqueBrands = uniq(selectedBrands.value);
    selectedDataByCategory.value = {};

    return props.data
      .filter((item) => {
        const isIncluded = uniqueBrands.includes(item.name);
        if (isIncluded) {
          selectedDataByCategory.value[item.name] = item;
        }
        return isIncluded;
      })
      .map((item) => ({
        name: item.name,
        isPickedAll: brandSelectAllIndex.value[item.name],
      }));
  });

  const selectedAggregated = computed(() =>
    selectedBrandsUnique.value
      .map((dataItem) =>
        dataItem.isPickedAll
          ? [{
              brand: dataItem.name,
              name: null as string | null,
              picked: true,
            }]
          : selected.value.filter((item) => item.brand === dataItem.name),
      )
      .reduce((left, right) => left.concat(right), [] as Array<{
        brand: string;
        name: string | null;
        picked: boolean;
      }>),
  );

  watch(
    () => props.data,
    () => {
      if (props.conditions[props.prefix]) {
        updateSelected(props.conditions[props.prefix].values);
      }
    },
  );

  watch(
    selected,
    (value) => {
      const groupsAggregated = selectedAggregated.value
        .filter((item) => !item.name)
        .map((item) => item.brand);

      const namesAggregated = selectedAggregated.value
        .filter((item) => item.name)
        .map((item) => item.name as string);

      if (selected.value) {
        props.conditions[props.prefix].values = groupsAggregated.concat(namesAggregated);
      }

      if (isEmpty(focusedItem.value) && value?.[0]) {
        focusedItem.value = {
          ...selectedDataByCategory.value[value[0]?.brand],
        };
      }
    },
    { deep: true },
  );

  watch(predicate, (value) => {
    props.conditions[props.prefix].predicate = value;
  });

  watch(includeUnknown, (value) => {
    props.conditions[props.prefix].includeUnknown = value;
  });

  watch(
    () => props.conditions,
    () => {
      if (!props.conditions[props.prefix]) {
        props.conditions[props.prefix] = {
          values: [],
          predicate: 'EQUALS',
        };
      }
    },
    { immediate: true },
  );

  watch(
    selectedKeysIndex,
    (value) => {
      $emitter.emit(`${props.prefix}-selected-keys-index`, value);
    },
    { deep: true },
  );

  watch(
    brandSelectAllIndex,
    (value) => {
      $emitter.emit(`${props.prefix}-brand-select-all-index`, value);
    },
    { deep: true },
  );

  onMounted(() => {
    if (props.conditions[props.prefix]) {
      predicate.value = props.conditions[props.prefix].predicate;
      includeUnknown.value = Boolean(props.conditions[props.prefix].includeUnknown);
    }
  });

  return {
    t,
    $colors,
    focusedItem,
    selected,
    predicate,
    includeUnknown,
    brandSelectAllIndex,
    panelPredicates,
    selectedKeysIndex,
    selectedBrands,
    subItems,
    selectedAggregated,
    toggle,
    selectAll,
    remove,
  };
};
